import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  PlusIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { apiService } from '../../services/api';

interface Plan {
  id: string;
  title: string;
  description: string;
  status: 'active' | 'completed' | 'planned' | 'on_hold';
  priority: 'high' | 'medium' | 'low';
  category: 'feature' | 'enhancement' | 'bug_fix' | 'infrastructure' | 'integration';
  timeline: string;
  progress: number;
  created_at: string;
  updated_at: string;
  owner: string;
  tags: string[];
}

interface Project {
  id: string;
  name: string;
  goal: string;
  status: string;
  created_at: string;
}

interface TrackerTask {
  id: string;
  title: string;
  body?: string | null;
  status: string;
  priority: number;
  labels?: string[] | null;
  due_date?: string | null;
  created_at: string;
  updated_at: string;
  assignee?: string | null;
}

interface TrackerOverview {
  project: Project;
  latest_self_check: Record<string, unknown> | null;
  latest_env_snapshot: Record<string, unknown> | null;
  next_milestone: Record<string, unknown> | null;
  recent_runs: Array<Record<string, unknown>>;
  open_errors: Array<Record<string, unknown>>;
  active_tasks: TrackerTask[];
}

const PlansPage: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { isLoading, error } = useQuery<TrackerOverview>({
    queryKey: ['tracker-overview'],
    queryFn: async (): Promise<TrackerOverview> => {
      const response = await apiService.get('/tracker/overview');
      return response as TrackerOverview;
    },
    refetchInterval: 30000,
  });

  // Fetch plans from tasks API (tasks with 'plan' label or high-level status)
  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['tracker-plans'],
    queryFn: async (): Promise<Plan[]> => {
      // For now, fetch tasks and convert to Plan format
      // In the future, could add a dedicated /tracker/plans endpoint
      const tasks = (await apiService.get('/tracker/tasks')) as TrackerTask[];
      return tasks.map((task) => ({
        id: task.id,
        title: task.title,
        description: task.body ?? '',
        status:
          task.status === 'done'
            ? 'completed'
            : task.status === 'in_progress'
              ? 'active'
              : 'planned',
        priority: task.priority <= 2 ? 'high' : task.priority <= 4 ? 'medium' : 'low',
        category: (task.labels ?? []).includes('feature')
          ? 'feature'
          : (task.labels ?? []).includes('bug')
            ? 'bug_fix'
            : (task.labels ?? []).includes('infrastructure')
              ? 'infrastructure'
              : 'enhancement',
        timeline: task.due_date
          ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : 'TBD',
        progress: task.status === 'done' ? 100 : task.status === 'in_progress' ? 50 : 0,
        created_at: task.created_at,
        updated_at: task.updated_at,
        owner: task.assignee ?? 'Unassigned',
        tags: task.labels ?? [],
      }));
    },
    refetchInterval: 30000,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'planned':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'on_hold':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'feature':
        return <StarIcon className="h-5 w-5" />;
      case 'enhancement':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'bug_fix':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'infrastructure':
        return <FlagIcon className="h-5 w-5" />;
      case 'integration':
        return <CheckCircleIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'active':
        return <ClockIcon className="h-5 w-5" />;
      case 'planned':
        return <FlagIcon className="h-5 w-5" />;
      case 'on_hold':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const filteredPlans = plans.filter((plan) => {
    const matchesStatus = filterStatus === 'all' || plan.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || plan.priority === filterPriority;
    const matchesSearch =
      searchTerm === '' ||
      plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesStatus && matchesPriority && matchesSearch;
  });

  const activePlans = filteredPlans.filter((p) => p.status === 'active');
  const completedPlans = filteredPlans.filter((p) => p.status === 'completed');
  const plannedPlans = filteredPlans.filter((p) => p.status === 'planned');

  const totalProgress = Math.round(
    plans.reduce((sum, plan) => sum + plan.progress, 0) / plans.length
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading plans data</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Development Plans</h1>
          <p className="text-gray-600">Strategic development roadmap and feature planning</p>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedPlans.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">{activePlans.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FlagIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Planned</p>
              <p className="text-2xl font-bold text-gray-900">{plannedPlans.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-gray-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Overall Progress</p>
              <p className="text-2xl font-bold text-gray-900">{totalProgress}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search plans, descriptions, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div className="flex space-x-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="planned">Planned</option>
              <option value="on_hold">On Hold</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All Priority</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Development Plans ({filteredPlans.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredPlans.map((plan) => (
            <div key={plan.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`flex-shrink-0 ${getStatusColor(plan.status)} p-1 rounded-md`}>
                    {getStatusIcon(plan.status)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{plan.title}</h4>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}
                      >
                        {plan.status.replace('_', ' ')}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(plan.priority)}`}
                      >
                        {plan.priority} priority
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-3">{plan.description}</p>

                    {/* Progress Bar */}
                    {plan.status === 'active' && (
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Progress</span>
                          <span>{plan.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${plan.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          {getCategoryIcon(plan.category)}
                          <span className="ml-1">{plan.category.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {plan.timeline}
                        </div>
                        <span>Owner: {plan.owner}</span>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {plan.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPlans.length === 0 && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No plans found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' || filterPriority !== 'all'
                ? 'Try adjusting your search criteria or filters.'
                : 'Get started by creating your first development plan.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansPage;
